import { supabase } from '../lib/supabase';

const API_BASE_URL = 'https://mobile.padrinho.app';

// Helper to get access token - prefers passed token, falls back to getSession
async function getAccessToken(providedToken?: string): Promise<string | null> {
  if (providedToken) return providedToken;
  
  try {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token || null;
  } catch (error) {
    console.error('❌ Error getting access token:', error);
    return null;
  }
}

export interface NotificationData {
  id?: string;
  onesignal_notification_id?: string;
  title: string;
  subtitle?: string;
  message: string;
  banner_url?: string;
  link_url?: string;
  icon?: string;
  background_color?: string;
  notification_type?: string;
  target_type: 'all' | 'user_ids' | 'segments' | 'tags';
  target_user_ids?: string[];
  target_segments?: string[];
  target_tags?: Record<string, any>;
  sent_at?: string;
  expires_at?: string;
  is_active?: boolean;
}

export interface UserNotificationStatus {
  id: string;
  notification_id: string;
  is_read: boolean;
  read_at?: string;
  notification: NotificationData;
}

export class NotificationsService {
  /**
   * Create a new notification via server API (bypasses RLS)
   * Uses the server-side endpoint which has access to supabaseAdmin
   */
  static async createNotification(notificationData: NotificationData): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      console.log('📝 NotificationsService: Creating notification via API:', {
        title: notificationData.title,
        oneSignalId: notificationData.onesignal_notification_id,
        targetType: notificationData.target_type
      });

      // Validate required fields
      if (!notificationData.title || !notificationData.message) {
        const error = 'Title and message are required';
        console.error('❌ Validation failed:', error);
        return { success: false, error };
      }

      // Call server API endpoint (no auth needed - server handles it)
      const response = await fetch(`${API_BASE_URL}/api/notifications/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(notificationData),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        console.error('❌ NotificationsService: API error creating notification:', result.error);
        return { success: false, error: result.error || 'Failed to create notification' };
      }

      console.log('✅ NotificationsService: Notification created via API:', result.data?.id);
      return { success: true, data: result.data };
    } catch (error: any) {
      console.error('❌ Exception in createNotification:', error);
      return { success: false, error: error.message || 'Unknown error' };
    }
  }

  /**
   * Create user notification status via server API (bypasses RLS)
   */
  static async createUserNotificationStatusViaAPI(
    notificationId: string,
    userId: string,
    isRead: boolean = false
  ): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      console.log('📝 NotificationsService: Creating user notification status via API:', {
        notificationId,
        userId
      });

      const response = await fetch(`${API_BASE_URL}/api/notifications/user-status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          notification_id: notificationId,
          user_id: userId,
          is_read: isRead
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        console.error('❌ NotificationsService: API error creating user status:', result.error);
        return { success: false, error: result.error || 'Failed to create user notification status' };
      }

      console.log('✅ NotificationsService: User notification status created via API');
      return { success: true, data: result.data };
    } catch (error: any) {
      console.error('❌ Exception in createUserNotificationStatusViaAPI:', error);
      return { success: false, error: error.message || 'Unknown error' };
    }
  }

  /**
   * Get notifications for the current user via API
   * @param accessToken - Optional access token (preferred for avoiding getSession() hangs)
   */
  static async getUserNotifications(accessToken?: string): Promise<{ success: boolean; data?: UserNotificationStatus[]; error?: string }> {
    try {
      const token = await getAccessToken(accessToken);
      if (!token) {
        return { success: false, error: 'User not authenticated' };
      }

      console.log('📱 Fetching notifications via API');

      const response = await fetch(`${API_BASE_URL}/api/notifications/list`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        console.error('❌ API error fetching notifications:', result.error);
        return { success: false, error: result.error || 'Failed to fetch notifications' };
      }

      console.log('✅ Found notifications via API:', result.data?.length || 0);
      return { success: true, data: result.data || [] };
    } catch (error: any) {
      console.error('❌ Exception fetching notifications:', error);
      return { success: false, error: error.message || 'Unknown error' };
    }
  }

  /**
   * Mark a notification as read via API
   * @param notificationId - The notification ID to mark as read
   * @param accessToken - Optional access token (preferred for avoiding getSession() hangs)
   */
  static async markAsRead(notificationId: string, accessToken?: string): Promise<{ success: boolean; error?: string }> {
    try {
      if (!notificationId) {
        return { success: false, error: 'Notification ID is required' };
      }

      const token = await getAccessToken(accessToken);
      if (!token) {
        return { success: false, error: 'User not authenticated' };
      }

      console.log('✅ Marking notification as read via API:', notificationId);

      const response = await fetch(`${API_BASE_URL}/api/notifications/mark-read`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ notification_id: notificationId })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        console.error('❌ API error marking notification as read:', result.error);
        return { success: false, error: result.error || 'Failed to mark as read' };
      }

      console.log('✅ Notification marked as read successfully via API');
      
      return { success: true };
    } catch (error: any) {
      console.error('❌ Exception marking notification as read:', error);
      return { success: false, error: error.message || 'Unknown error occurred' };
    }
  }

  /**
   * Get unread notification count via API
   * @param accessToken - Optional access token (preferred for avoiding getSession() hangs)
   */
  static async getUnreadCount(accessToken?: string): Promise<{ success: boolean; count?: number; error?: string }> {
    try {
      const token = await getAccessToken(accessToken);
      if (!token) {
        return { success: false, error: 'User not authenticated' };
      }

      console.log('📊 Fetching unread count via API');

      const response = await fetch(`${API_BASE_URL}/api/notifications/unread-count`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        console.error('❌ API error fetching unread count:', result.error);
        return { success: false, error: result.error || 'Failed to fetch unread count' };
      }

      console.log('📊 Unread count via API:', result.count);
      return { success: true, count: result.count };
    } catch (error: any) {
      console.error('❌ Exception getting unread count:', error);
      return { success: false, error: error.message || 'Unknown error' };
    }
  }
}
